import './waterMark.scss';
import type { IWaterMarkProps } from '@umu/umu-types';
// biome-ignore lint/correctness/noUnusedImports: required for apps using jsx: "react" (legacy JSX transform)
import React, { useMemo } from 'react';

interface TileInfo {
    svgDataUrl: string;
    cssW: number;
    cssH: number;
}

function escapeXml(str: string): string {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
}

function measureTextWidth(text: string, size: number, font: string): number {
    if (typeof document === 'undefined') return 0;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        canvas.width = 0;
        return 0;
    }
    ctx.font = `${size}px ${font}`;
    const width = Math.ceil(ctx.measureText(text).width);
    canvas.width = 0;
    return width;
}

export function WaterMarkSvg(props: IWaterMarkProps) {
    const {
        text = 'UMU',
        color = '#999',
        rotate: degree = 30,
        font = 'sans-serif',
        trasparency = 0.2,
        isShowWaterMark = false,
        size = 18,
        offsetY = 100,
        offsetX: space = 50,
    } = props;

    const tile: TileInfo | null = useMemo(() => {
        if (!(isShowWaterMark && text) || typeof document === 'undefined') return null;

        const textW = measureTextWidth(text, size, font);
        if (textW <= 0) return null;

        // 旋转后的包围盒：按任意角度 + 任意长度文本计算，避免长文本被 tile 裁剪
        const rad = (degree * Math.PI) / 180;
        const cosT = Math.abs(Math.cos(rad));
        const sinT = Math.abs(Math.sin(rad));
        const bboxW = textW * cosT + size * sinT;
        const bboxH = textW * sinT + size * cosT;

        // tile = 旋转包围盒 + 间距（水平 space、垂直 offsetY）
        const tileW = Math.ceil(bboxW + space);
        const tileH = Math.ceil(bboxH + offsetY);
        const cx = tileW / 2;
        const cy = tileH / 2;

        const svgString =
            `<svg xmlns='http://www.w3.org/2000/svg' width='${tileW}' height='${tileH}'>` +
            `<text x='${cx}' y='${cy}' ` +
            `fill='${escapeXml(color)}' fill-opacity='${trasparency}' ` +
            `transform='rotate(-${degree} ${cx} ${cy})' ` +
            `font-family='${escapeXml(font)}' font-size='${size}' ` +
            `text-anchor='middle' dominant-baseline='middle'>${escapeXml(text)}</text>` +
            '</svg>';

        return {
            svgDataUrl: `url("data:image/svg+xml;utf8,${encodeURIComponent(svgString)}")`,
            cssW: tileW,
            cssH: tileH,
        };
    }, [text, size, font, color, trasparency, degree, space, offsetY, isShowWaterMark]);

    if (!(isShowWaterMark && tile)) return null;

    return (
        <div
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                backgroundImage: tile.svgDataUrl,
                backgroundRepeat: 'repeat',
                backgroundSize: `${tile.cssW}px ${tile.cssH}px`,
            }}
        />
    );
}
